import { NavLink, Link } from "react-router-dom";
import { useState } from "react";
import { Menu, X, GraduationCap } from "lucide-react";

const navItems = [
  { path: "/", label: "Overview" },
  { path: "/pillars", label: "8 Pillars" },
  { path: "/methods", label: "Methods" },
  { path: "/assessment", label: "Assessment" },
  { path: "/benchmarks", label: "Benchmarks" },
  { path: "/focus", label: "Focus Areas" },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 shadow-sm">
        <div className="container mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary">
              <GraduationCap className="w-5 h-5 text-primary-foreground" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-bold text-primary tracking-wide">GBSRC</p>
              <p className="text-[10px] text-muted-foreground hidden sm:block">Pedagogical Framework</p>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Accreditation badges */}
          <div className="hidden lg:flex items-center gap-1">
            {["AACSB", "EFMD", "NEP 2020"].map((b) => (
              <span
                key={b}
                className="text-[10px] font-bold border border-primary/30 text-primary px-2 py-0.5 rounded"
              >
                {b}
              </span>
            ))}
          </div>

          {/* Mobile menu */}
          <button
            className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground"
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <nav className="md:hidden border-t border-border bg-background px-4 py-2 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      {/* MAIN */}
      <main className="flex-1">{children}</main>

      {/* FOOTER */}
      <footer className="border-t border-border bg-muted/40 py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center items-center gap-2 mb-2">
            <GraduationCap className="w-5 h-5 text-primary" />
            <span className="font-bold text-primary">GBSRC</span>
            <span className="text-muted-foreground text-sm">— Global Business School &amp; Research Centre</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Pedagogical Framework &nbsp;|&nbsp; Academic Year 2025–26 &nbsp;|&nbsp; Aligned with AACSB · EFMD · EQUIS · NEP 2020 · OBE
          </p>
        </div>
      </footer>
    </div>
  );
}
